import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type LectureSummary } from "../api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { Menu } from "../components/Menu.js";

/** "https://www.youtube.com/@example/streams" → "@example/streams": the channel a watcher tab is on */
const channelOf = (url: string) => url.replace(/^https?:\/\/(www\.)?youtube\.com\//, "");
const STAGE_LABEL: Record<string, string> = { ingested: "Downloading", transcribed: "Transcribing", translated: "Translating", selected: "Clips ready" };

export const Library: React.FC = () => {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.lectures>> | null>(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const load = () => api.lectures().then(setData).catch((e) => toast(e.message));
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, []);
  const add = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try { await api.addLecture(url.trim()); setUrl(""); toast("Lecture queued"); load(); } catch (e: any) { toast(e.message); } finally { setBusy(false); }
  };
  const [showArchived, setShowArchived] = useState(false);
  const all = data?.lectures ?? [];
  const archivedCount = all.filter((l) => l.archived).length;
  const lectures = all.filter((l) => (showArchived ? true : !l.archived));
  const live = all.filter((l) => !l.archived);
  const clips = live.reduce((a, l) => a + l.clips.total, 0);
  const toPost = live.reduce((a, l) => a + Math.max(0, l.clips.rendered - l.clips.posted), 0);
  const w = data?.watcher;
  return (
    <div className="main">
      <div className="row" style={{ flex: 1, minHeight: 0, gap: 0, alignItems: "stretch" }}>
        <div className="col" style={{ flex: 1, minWidth: 0, gap: 0 }}>
          <div className="page-head">
            <div><h1>Library</h1><div className="sub">{data ? `${live.length} lecture${live.length === 1 ? "" : "s"} · ${clips} clip${clips === 1 ? "" : "s"}${toPost ? ` · ${toPost} to post` : ""}` : "Loading…"}</div></div>
            <span className="spacer" />
            <input style={{ flex: "1 1 260px", maxWidth: 440, padding: "10px 14px", borderRadius: 10 }} placeholder="Paste a YouTube link to add a lecture" aria-label="YouTube link" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
            <button className="btn primary" onClick={add} disabled={busy || !url.trim()}><Icon name="plus" size={14} />Add lecture</button>
          </div>
          <div className="stats"><span>{w ? `Watcher ${w.enabled ? "on" : "off"}` : ""}</span>{archivedCount ? <button className="chip" onClick={() => setShowArchived(!showArchived)}>{showArchived ? "Hide archived" : `${archivedCount} archived`}</button> : null}</div>
          <div className="page col" style={{ gap: 10 }}>
            {data?.pending.map((p) => (
              <div key={p.videoId} className="lect" style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
                <div className="col" style={{ gap: 6, minWidth: 0 }}><span className="t">{p.url as string}</span><div className="progress"><i style={{ width: `${Math.round(p.progress * 100)}%` }} /></div></div>
                <span className="chip gold">{p.status === "running" ? "Processing…" : "Queued"}</span>
              </div>
            ))}
            {lectures.map((l) => <LectureRow key={l.videoId} l={l} onChange={load} />)}
            {data && !lectures.length && !data.pending.length ? <div className="muted" style={{ padding: 40, textAlign: "center" }}>No lectures yet. Paste a YouTube link above.</div> : null}
          </div>
        </div>
        <aside className="side">
          <span className="k">Clips</span>
          <span className="v">{clips}</span>
          <span className="sub">across {live.length} lecture{live.length === 1 ? "" : "s"}{toPost ? ` · ${toPost} to post` : ""}</span>
          <span className="k" style={{ marginTop: 14 }}>Watcher</span>
          <span className="sub" title={w?.lastError ?? undefined}>{w ? `${w.enabled ? "On" : "Off"} · checks ${w.tabs.length ? w.tabs.map(channelOf).join(", ") : "no channel yet"}${w.lastCheck ? ` · last ${new Date(w.lastCheck).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}` : "…"}</span>
          <button className="btn sm" style={{ alignSelf: "flex-start" }} onClick={() => api.pollWatcher().then((r) => toast(r.added.length ? `Queued ${r.added.length} new` : "Nothing new")).catch((e) => toast(e.message))}><Icon name="refresh" size={14} />Check now</button>
          {archivedCount ? <>
            <span className="k" style={{ marginTop: 14 }}>Archived</span>
            <button className={`chip${showArchived ? " gold" : ""}`} style={{ alignSelf: "flex-start" }} onClick={() => setShowArchived(!showArchived)} title="Archived lectures are finished ones tucked away to keep the list clean">{showArchived ? "Hide archived" : `Show ${archivedCount} archived`}</button>
          </> : null}
        </aside>
      </div>
    </div>
  );
};

/** One lecture: cover, title, what state its clips are in, the one thing to do next, and a menu for the rest (archive, YouTube). */
const LectureRow: React.FC<{ l: LectureSummary; onChange: () => void }> = ({ l, onChange }) => {
  const toast = useToast();
  const running = l.jobs.find((j) => j.status === "running");
  const review = Math.max(0, l.clips.total - l.clips.approved - l.clips.rendered - l.clips.posted);
  const ready = l.stage === "selected";
  const to = `/lectures/${l.videoId}`;
  const archive = () => api.archive(l.videoId, !l.archived).then(() => { toast(l.archived ? "Back in the library" : "Archived · find it under Show archived"); onChange(); }).catch((e) => toast(e.message));
  return (
    <div className="lect">
      <Link to={to} className="cover" title="Open the lecture"><img src={`https://i.ytimg.com/vi/${l.videoId}/mqdefault.jpg`} alt="" loading="lazy" /></Link>
      <Link to={to} style={{ minWidth: 0, color: "inherit" }}>
        <div className="t">{l.title}</div>
        <div className="m">{l.speaker} · {l.lang === "ar" ? "Arabic" : "English"} · {Math.round(l.durationMs / 60000)} min</div>
        <div className="pills">
          {!ready ? <span className="chip gold">{STAGE_LABEL[l.stage] ?? l.stage}{running ? ` · ${Math.round(running.progress * 100)}%` : ""}</span> : null}
          {ready && review ? <span className="chip warn">{review} to review</span> : null}
          {ready && l.clips.total ? <span className="chip green">{l.clips.total} clip{l.clips.total === 1 ? "" : "s"}</span> : null}
          {l.clips.rendered ? <span className="chip">{l.clips.rendered} rendered</span> : null}
          {l.clips.rendered - l.clips.posted > 0 ? <span className="chip gold">{l.clips.rendered - l.clips.posted} to post</span> : null}
          {l.clips.posted ? <span className="chip green">{l.clips.posted} posted</span> : null}
          {l.clips.flagged ? <span className="chip warn">{l.clips.flagged} flagged</span> : null}
          {ready && running ? <span className="chip gold">{running.type}… {Math.round(running.progress * 100)}%</span> : null}
          {l.archived ? <span className="chip">Archived</span> : null}
        </div>
      </Link>
      <div className="row" style={{ gap: 6 }}>
        <Link to={to} className={`btn sm${ready && review ? " gold" : ""}`}>{ready && review ? "Review" : "Open"}</Link>
        <Menu title="More">
          <button onClick={archive}><Icon name="grid" size={14} />{l.archived ? "Bring back to the library" : "Archive this lecture"}<small>{l.archived ? "" : "tucked away, nothing deleted"}</small></button>
          <a href={l.url} target="_blank" rel="noreferrer"><Icon name="eye" size={14} />Open on YouTube</a>
        </Menu>
      </div>
    </div>
  );
};
